import React from "react";
import { CaretDown } from "@phosphor-icons/react";
import { colors2, UI_CAPTION, UI_CAPTION_SEMIBOLD } from "oolib";

/**
 * Vertical pair rendering — reads top-down through the directional arrow:
 *   TagA (semibold, up to 2 lines then ellipsis)
 *   SubThemeA (smaller, grey, 1 line)
 *   ↓ (caret)
 *   TagB (semibold, up to 2 lines then ellipsis)
 *   SubThemeB (smaller, grey, 1 line)
 *
 * Reads `tagA` and `tagB` objects directly from tableRowData (each has
 * `display`, `subThemeDisplay`). The `value` prop is ignored.
 *
 * Designed for tables that opt into a taller `tableConfig.rowHeight` — tag
 * lines clamp at 2 so the row height stays predictable per pair.
 *
 * Set `colConfig.valuePath: "tagA"` (or any truthy row field) so TableCell's
 * blockIsPopulatedConditions check doesn't fall back to a "-".
 *
 * `hideTagB` (passed via colConfig.props): collapses to a single tag block
 * (tagA only — no caret, no tagB). Used by "anchored" views where the BE
 * pins one side of the pair to a fixed tag (e.g. negativePulse anchors
 * tagB = Negative sentiment) and the user only needs to see the variable
 * side.
 */
const TablePairDisplay = ({ tableRowData, hideTagB }) => {
  const tagA = tableRowData?.tagA;
  const tagB = tableRowData?.tagB;
  if (!tagA || (!hideTagB && !tagB)) return null;

  // Two-line clamp for tag titles. Single-line ellipsis for subThemes.
  // `WebkitLineClamp` only works in concert with the box display props below.
  // `whiteSpace: normal` overrides any nowrap inherited from TableCell wrapper.
  const tagClampStyle = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
    whiteSpace: "normal",
  };
  const subThemeEllipsisStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    display: "block",
    width: "100%",
  };

  const renderTagBlock = (tag) => (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0, width: "100%" }}>
      <UI_CAPTION_SEMIBOLD style={{ ...tagClampStyle, color: colors2.grey80 }}>
        {tag.display}
      </UI_CAPTION_SEMIBOLD>
      {tag.subThemeDisplay && (
        <UI_CAPTION style={{ ...subThemeEllipsisStyle, color: colors2.grey60 }}>
          {tag.subThemeDisplay}
        </UI_CAPTION>
      )}
    </div>
  );

  const titleText = hideTagB
    ? `${tagA.display} (${tagA.subThemeDisplay || ""})`
    : `${tagA.display} (${tagA.subThemeDisplay || ""})  →  ${tagB.display} (${tagB.subThemeDisplay || ""})`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "0.2rem",
        width: "100%",
        height: "100%",
      }}
      title={titleText}
    >
      {renderTagBlock(tagA)}
      {!hideTagB && (
        <>
          {/* Direction = stronger Predictability (set by the BE — symmetric
              metrics like Count/Strength/Overlap are unaffected by order). */}
          <CaretDown size={12} weight="bold" color={colors2.grey40} />
          {renderTagBlock(tagB)}
        </>
      )}
    </div>
  );
};

export default TablePairDisplay;
